import * as Sentry from "@sentry/browser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import api, { apiWithoutCamelization, type ApiResponse } from "../api";
import type { ConfiguratorComponent } from "../components/ComponentListContainer/ComponentList/ComponentTable";
import { connectionQueries } from "../components/ConnectionWizard/hooks/useConnection";
import { COMPONENT_QUERY_KEY } from "../utils/constants";
import { ObjectName } from "../utils/enums";
import { getObjectListUrl } from "../utils/getObjectListUrl";
import { personQueries } from "./usePerson";

export function useConfiguratorComponents<T = ConfiguratorComponent>(
  objectName: ObjectName,
  siteOrVariantId?: number,
  enabled = true
) {
  const cacheName = COMPONENT_QUERY_KEY[objectName];
  const queryClient = useQueryClient();

  const queryKey =
    objectName === ObjectName.Person
      ? personQueries.list({ variantId: siteOrVariantId }).queryKey
      : objectName === ObjectName.Connection
        ? connectionQueries.list({ siteOrVariantId }).queryKey
        : [cacheName, { objectName, siteOrVariantId: siteOrVariantId }];

  const { isLoading, error, data } = useQuery({
    queryKey,
    queryFn: () => {
      if (typeof siteOrVariantId !== "number") {
        Sentry.captureMessage(
          `useConfiguratorComponents queryFn called without a numeric siteOrVariantId for objectName ${objectName}`
        );
        throw new Error(t("errors.ServerError"));
      }

      return fetchConfiguratorComponents<T>(objectName, siteOrVariantId);
    },
    staleTime: 10000,
    enabled: enabled && typeof siteOrVariantId === "number"
  });

  function invalidateConfiguratorComponents() {
    return queryClient.invalidateQueries({ queryKey });
  }

  return { isLoading, error, data, invalidateConfiguratorComponents };
}

async function fetchConfiguratorComponents<T>(
  objectName: ObjectName,
  siteOrVariantId: number
) {
  const url: string = getObjectListUrl(objectName, siteOrVariantId);
  let response: ApiResponse<Array<T>>;

  if (objectName === ObjectName.GasConnection) {
    response = await apiWithoutCamelization.get<Array<T>>(url);
  } else {
    response = await api.get<Array<T>>(url);
  }

  return response.data;
}
